import {
  activeSegmentIndex,
  buildTimeline,
  clamp,
  handoffProgress,
  journeyProgress,
  lingerEase,
  localProgress,
  mediaSnapProgress,
  nearestSceneIndex,
  nearestSnapIndex,
  sceneSnapAnchors,
  smoothstep,
  type TimelineSegment,
} from './timeline';

interface LinkConfig {
  label: string;
  href: string;
}

interface SceneCta {
  primary?: LinkConfig;
  secondary?: LinkConfig;
}

export interface ScrollWorldScene {
  id: string;
  label: string;
  still: string;
  mobileStill?: string;
  clip?: string;
  mobileClip?: string;
  accent: string;
  title: string;
  body: string;
  tags: string[];
  scrollUnits?: number;
  linger?: number;
  cta?: SceneCta;
  prototypeVisual?: boolean;
}

export interface ScrollWorldMasterMedia {
  clip: string;
  mobileClip?: string;
  webmClip?: string;
  mobileWebmClip?: string;
  poster: string;
  mobilePoster?: string;
}

export interface ScrollWorldConfig {
  brand: {
    name: string;
    href: string;
    mark: string;
  };
  primaryCta: LinkConfig;
  hint: string;
  scenes: ScrollWorldScene[];
  masterMedia?: ScrollWorldMasterMedia;
  connectors?: Array<string | undefined>;
  mobileConnectors?: Array<string | undefined>;
  sceneUnits?: number;
  connectorUnits?: number;
  crossfadeUnits?: number;
  handoffUnits?: number;
  seekSmoothing?: number;
}

interface RuntimeSegment extends TimelineSegment {
  element: HTMLElement;
  image: HTMLImageElement;
  video?: HTMLVideoElement;
  blobUrl?: string;
  loading: boolean;
  ready: boolean;
  visible: boolean;
  current: number;
  target: number;
}

interface MasterRuntime {
  element: HTMLElement;
  image: HTMLImageElement;
  video?: HTMLVideoElement;
  blobUrl?: string;
  loading: boolean;
  ready: boolean;
  current: number;
  target: number;
}

interface SceneDom {
  copy: HTMLElement;
  navButton: HTMLButtonElement;
  routeButton: HTMLButtonElement;
}

interface DebugDom {
  root: HTMLElement;
  scene: HTMLElement;
  global: HTMLElement;
  local: HTMLElement;
  media: HTMLElement;
  flags: HTMLElement;
  state: HTMLElement;
  event: HTMLElement;
}

interface MagneticTransition {
  from: number;
  to: number;
  token: number;
  kind: 'forward' | 'reverse' | 'direct' | 'handoff';
}

const MOBILE_QUERY = '(max-width: 860px)';

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

function appendText<K extends keyof HTMLElementTagNameMap>(
  parent: HTMLElement,
  tag: K,
  className: string,
  text: string,
): HTMLElementTagNameMap[K] {
  const node = element(tag, className);
  node.textContent = text;
  parent.append(node);
  return node;
}

function appendCta(parent: HTMLElement, link: LinkConfig, variant: 'primary' | 'ghost'): void {
  const anchor = element('a', `world-button world-button--${variant}`);
  anchor.href = link.href;
  anchor.textContent = link.label;
  parent.append(anchor);
}

function preferredPoster(scene: ScrollWorldScene, mobile: boolean): string {
  return mobile && scene.mobileStill ? scene.mobileStill : scene.still;
}

function buildPrototypeVisual(scene: ScrollWorldScene, sceneIndex: number): HTMLElement {
  const host = element('div', 'world-animatic');
  host.dataset.scene = scene.id;
  host.style.setProperty('--scene-index', String(sceneIndex));

  const atmosphere = element('div', 'animatic-atmosphere');
  const architecture = element('div', 'animatic-architecture');
  for (let index = 0; index < 3; index += 1) {
    const arch = element('i', `animatic-arch animatic-arch--${index + 1}`);
    architecture.append(arch);
  }

  const field = element('div', 'animatic-field');
  for (let index = 0; index < 12; index += 1) {
    const node = element('i', 'animatic-node');
    node.style.setProperty('--node-index', String(index));
    field.append(node);
  }

  const figures = element('div', 'animatic-figures');
  for (const material of ['marble', 'water']) {
    const figure = element('div', `animatic-figure animatic-figure--${material}`);
    const cloak = element('div', 'animatic-cloak');
    for (let index = 0; index < 9; index += 1) {
      const particle = element('i', 'animatic-particle');
      particle.style.setProperty('--particle-index', String(index));
      particle.style.setProperty('--particle-x', `${9 + index * 9}%`);
      particle.style.setProperty('--particle-y', `${4 + index * 5}%`);
      cloak.append(particle);
    }
    figure.append(cloak, element('div', 'animatic-head'), element('div', 'animatic-body'));
    figures.append(figure);
  }

  const coordinate = element('div', 'animatic-coordinate');
  coordinate.append(element('i'), element('i'), element('i'));
  host.append(atmosphere, architecture, field, coordinate, figures);
  return host;
}

function buildSceneCopy(scene: ScrollWorldScene, index: number): HTMLElement {
  const article = element('article', 'world-copy');
  article.id = `scene-${scene.id}`;
  article.style.setProperty('--accent', scene.accent);
  article.setAttribute('aria-hidden', 'true');

  const titleTag = index === 0 ? 'h1' : 'h2';
  appendText(article, titleTag, 'world-copy__title', scene.title);
  appendText(article, 'p', 'world-copy__body', scene.body);

  if (scene.tags.length > 0) {
    const tags = element('ul', 'world-copy__tags');
    for (const tag of scene.tags) appendText(tags, 'li', '', tag);
    article.append(tags);
  }

  if (scene.cta) {
    const actions = element('div', 'world-copy__actions');
    if (scene.cta.primary) appendCta(actions, scene.cta.primary, 'primary');
    if (scene.cta.secondary) appendCta(actions, scene.cta.secondary, 'ghost');
    article.append(actions);
  }

  return article;
}

function createChrome(container: HTMLElement, config: ScrollWorldConfig) {
  const progress = element('div', 'world-progress');
  progress.setAttribute('aria-hidden', 'true');
  const progressFill = element('span');
  progress.append(progressFill);

  const header = element('header', 'world-header');
  const brand = element('a', 'world-brand');
  brand.href = config.brand.href;
  brand.setAttribute('aria-label', `${config.brand.name}, back to start`);
  const mark = element('img', 'world-brand__mark');
  mark.src = config.brand.mark;
  mark.alt = '';
  mark.width = 512;
  mark.height = 512;
  brand.append(mark);
  appendText(brand, 'span', 'world-brand__name', config.brand.name);

  const nav = element('nav', 'world-nav');
  nav.setAttribute('aria-label', 'Project journey');

  const contact = element('a', 'world-header__cta');
  contact.href = config.primaryCta.href;
  contact.textContent = config.primaryCta.label;

  header.append(brand, nav, contact);

  const stage = element('div', 'world-stage');
  stage.setAttribute('aria-hidden', 'true');

  const copyLayer = element('div', 'world-copy-layer');
  copyLayer.id = 'world-copy';

  const route = element('nav', 'world-route');
  route.setAttribute('aria-label', 'Scene navigation');

  const hint = element('div', 'world-hint');
  hint.setAttribute('aria-hidden', 'true');
  appendText(hint, 'span', '', config.hint);
  hint.append(element('i'));

  const track = element('div', 'world-track');
  track.setAttribute('aria-hidden', 'true');

  container.append(progress, header, stage, copyLayer, route, hint, track);
  return { progress, progressFill, header, nav, contact, stage, copyLayer, route, hint, track };
}

function createDebugOverlay(container: HTMLElement): DebugDom | undefined {
  const debugMode = new URLSearchParams(window.location.search).get('debug');
  if (debugMode !== 'scroll' && debugMode !== 'media') return undefined;

  const root = element('aside', 'world-debug');
  root.setAttribute('aria-label', 'Scroll timeline diagnostics');
  const title = appendText(root, 'strong', '', 'MEDIA / TIMELINE');
  title.setAttribute('aria-hidden', 'true');
  const scene = appendText(root, 'span', '', 'scene —');
  const global = appendText(root, 'span', '', 'global 0.000');
  const local = appendText(root, 'span', '', 'local 0.000');
  const media = appendText(root, 'span', '', 'media animatic');
  const flags = appendText(root, 'span', '', 'flags —');
  const state = appendText(root, 'span', '', 'state —');
  const event = appendText(root, 'span', '', 'event —');
  container.append(root);
  return { root, scene, global, local, media, flags, state, event };
}

export function mountScrollWorld(container: HTMLElement, config: ScrollWorldConfig): void {
  if (config.scenes.length === 0) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const mobileQuery = window.matchMedia(MOBILE_QUERY);
  const isMobile = () => coarsePointer || mobileQuery.matches;
  const sceneUnits = config.sceneUnits ?? 1.35;
  const connectorUnits = config.connectorUnits ?? 0.85;
  const crossfadeUnits = config.crossfadeUnits ?? 0.14;
  const handoffUnits = config.handoffUnits ?? 0.75;
  const seekSmoothing = clamp(config.seekSmoothing ?? 1);

  container.classList.add('scroll-world');
  const playHeroEntrance = !reducedMotion && window.scrollY < 8;
  if (playHeroEntrance) container.classList.add('is-hero-entering');
  container.id = 'top';
  const chrome = createChrome(container, config);
  const debug = createDebugOverlay(container);
  const timeline = buildTimeline(config.scenes, {
    sceneUnits,
    connectorUnits,
    connectors: config.connectors,
    mobileConnectors: config.mobileConnectors,
  });

  const masterRuntime: MasterRuntime | undefined = config.masterMedia
    ? (() => {
        const media = config.masterMedia;
        const master = element('div', 'world-master');
        const image = element('img', 'world-master__poster');
        image.alt = '';
        image.decoding = 'async';
        image.loading = 'eager';
        image.fetchPriority = 'high';
        image.src = isMobile() && media.mobilePoster ? media.mobilePoster : media.poster;
        master.append(image);
        chrome.stage.append(master);
        return {
          element: master,
          image,
          loading: false,
          ready: false,
          current: 0,
          target: 0,
        };
      })()
    : undefined;

  const sceneDom: SceneDom[] = [];
  const sceneSegments = new Map<number, RuntimeSegment>();
  const runtimeSegments: RuntimeSegment[] = timeline.map((segment, timelineIndex) => {
    if (masterRuntime) {
      const runtime: RuntimeSegment = {
        ...segment,
        element: masterRuntime.element,
        image: masterRuntime.image,
        loading: false,
        ready: false,
        visible: false,
        current: 0,
        target: 0,
      };
      if (segment.kind === 'scene') sceneSegments.set(segment.sceneIndex, runtime);
      return runtime;
    }

    const posterSceneIndex =
      segment.kind === 'connector'
        ? Math.min(segment.sceneIndex + 1, config.scenes.length - 1)
        : segment.sceneIndex;
    const posterScene = config.scenes[posterSceneIndex];
    if (!posterScene) throw new Error(`Missing scene ${posterSceneIndex}`);

    const sceneElement = element('div', 'world-scene');
    sceneElement.style.setProperty('--accent', posterScene.accent);
    sceneElement.style.zIndex = String(100 + timelineIndex);
    const image = element('img', 'world-scene__still');
    image.alt = '';
    image.decoding = 'async';
    image.loading = posterSceneIndex === 0 ? 'eager' : 'lazy';
    image.src = preferredPoster(posterScene, isMobile());
    sceneElement.append(image);
    if (posterScene.prototypeVisual) {
      sceneElement.classList.add('has-animatic');
      sceneElement.append(buildPrototypeVisual(posterScene, posterSceneIndex));
    }
    chrome.stage.append(sceneElement);

    const runtime: RuntimeSegment = {
      ...segment,
      element: sceneElement,
      image,
      loading: false,
      ready: false,
      visible: false,
      current: 0,
      target: 0,
    };
    if (segment.kind === 'scene') sceneSegments.set(segment.sceneIndex, runtime);
    return runtime;
  });

  config.scenes.forEach((scene, index) => {
    const copy = buildSceneCopy(scene, index);
    chrome.copyLayer.append(copy);

    const navButton = element('button', 'world-nav__item');
    navButton.type = 'button';
    navButton.textContent = scene.label;
    chrome.nav.append(navButton);

    const routeButton = element('button', 'world-route__item');
    routeButton.type = 'button';
    routeButton.setAttribute('aria-label', `Go to ${scene.label}`);
    routeButton.style.setProperty('--accent', scene.accent);
    appendText(routeButton, 'span', 'world-route__label', scene.label);
    routeButton.append(element('i'));
    chrome.route.append(routeButton);

    sceneDom.push({ copy, navButton, routeButton });
  });

  let viewportHeight = window.innerHeight;
  let laidOutWidth = window.innerWidth;
  let totalUnits = timeline.at(-1)?.end ?? 1;
  let ticking = false;
  let videoFramePending = false;
  let activeScene = -1;
  let gestureReceived = false;
  let snapAnchors = sceneSnapAnchors(runtimeSegments, config.scenes.length);
  let settledPoint = nearestSnapIndex(snapAnchors, window.scrollY / viewportHeight);
  let magneticTransition: MagneticTransition | undefined;
  let transitionToken = 0;
  let wheelTension = 0;
  let wheelResetTimer = 0;
  let touchStartY: number | undefined;
  let touchTriggered = false;
  let masterLoadPromise: Promise<boolean> | undefined;
  let mediaPhase = 'poster';
  let lastMediaEvent = 'none';
  let playState = 'idle';
  let loadedBytes = 0;

  const noteMediaEvent = (event: string): void => {
    lastMediaEvent = event;
    requestRead();
  };

  const primeVideo = (video?: HTMLVideoElement): void => {
    if (!isMobile() || !video) return;
    try {
      playState = 'prime pending';
      const promise = video.play();
      promise
        .then(() => {
          playState = 'prime allowed';
          if (!magneticTransition) video.pause();
        })
        .catch((error: unknown) => {
          playState = `prime denied ${error instanceof DOMException ? error.name : 'error'}`;
          requestRead();
        });
    } catch {
      playState = 'prime threw';
      // A poster remains visible if the browser refuses to prime the decoder.
    }
  };

  const loadVideo = (segment: RuntimeSegment): void => {
    if (reducedMotion || segment.loading || !segment.asset) return;
    segment.loading = true;
    const source = isMobile() && segment.mobileAsset ? segment.mobileAsset : segment.asset;

    fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error(`Video request failed: ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const video = element('video', 'world-scene__video');
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        segment.blobUrl = URL.createObjectURL(blob);
        video.src = segment.blobUrl;
        video.addEventListener('loadedmetadata', () => {
          segment.ready = true;
          read();
        });
        video.addEventListener('seeked', () => segment.element.classList.add('has-video'), {
          once: true,
        });
        video.addEventListener('loadeddata', () => {
          video.pause();
          if (gestureReceived) primeVideo(video);
        });
        segment.video = video;
        segment.element.append(video);
      })
      .catch(() => {
        segment.loading = false;
      });
  };

  const loadMasterVideo = (): void => {
    if (reducedMotion) {
      mediaPhase = 'blocked: reduce motion';
      return;
    }
    if (!masterRuntime || masterRuntime.loading || !config.masterMedia) return;
    masterRuntime.loading = true;
    mediaPhase = 'selecting source';
    const probe = document.createElement('video');
    const supportsH264 = Boolean(
      probe.canPlayType('video/mp4') || probe.canPlayType('video/mp4; codecs="avc1.64001f"'),
    );
    const supportsVp9 = Boolean(probe.canPlayType('video/webm; codecs="vp9"'));
    const useWebmFallback = !supportsH264 && supportsVp9;
    const source = isMobile()
      ? (config.masterMedia.mobileClip ?? config.masterMedia.clip)
      : useWebmFallback && config.masterMedia.webmClip
        ? config.masterMedia.webmClip
        : config.masterMedia.clip;

    let bufferingTimer = 0;
    const attachVideo = (videoSource: string, blobUrl?: string): void => {
      window.clearTimeout(bufferingTimer);
      mediaPhase = 'video attached';
      const video = element('video', 'world-master__video');
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.disablePictureInPicture = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      if (blobUrl) masterRuntime.blobUrl = blobUrl;

      const revealVideo = (): void => {
        masterRuntime.element.classList.remove('is-buffering');
        masterRuntime.element.classList.add('has-video');
      };

      video.addEventListener('loadedmetadata', () => {
        mediaPhase = 'metadata ready';
        noteMediaEvent('loadedmetadata');
        masterRuntime.ready = true;
        if (!magneticTransition) syncMasterToPoint(settledPoint);
        if (gestureReceived) primeVideo(video);
        read();
      });
      video.addEventListener(
        'loadeddata',
        () => {
          mediaPhase = 'frame ready';
          noteMediaEvent('loadeddata');
          revealVideo();
          if (!magneticTransition) video.pause();
          if (gestureReceived) primeVideo(video);
        },
        { once: true },
      );
      video.addEventListener(
        'canplay',
        () => {
          noteMediaEvent('canplay');
          revealVideo();
        },
        { once: true },
      );
      video.addEventListener('playing', () => {
        playState = 'playing';
        noteMediaEvent('playing');
        revealVideo();
      });
      video.addEventListener(
        'seeked',
        () => {
          noteMediaEvent('seeked');
          revealVideo();
        },
        { once: true },
      );
      video.addEventListener('waiting', () => noteMediaEvent('waiting'));
      video.addEventListener('stalled', () => noteMediaEvent('stalled'));
      video.addEventListener('suspend', () => noteMediaEvent('suspend'));
      video.addEventListener('error', () => {
        mediaPhase = `video error ${video.error?.code ?? 'unknown'}`;
        noteMediaEvent('error');
        masterRuntime.element.classList.remove('is-buffering');
        masterRuntime.loading = false;
        masterRuntime.element.classList.add('has-media-error');
      });
      video.src = videoSource;
      masterRuntime.video = video;
      masterRuntime.element.append(video);
      video.load();
    };

    if (isMobile()) {
      bufferingTimer = window.setTimeout(() => {
        masterRuntime.element.classList.add('is-buffering');
      }, 350);
    }

    masterLoadPromise = fetch(source)
      .then((response) => {
        if (!response.ok) throw new Error(`Master video request failed: ${response.status}`);
        mediaPhase = 'downloading';
        return response.blob();
      })
      .then((blob) => {
        loadedBytes = blob.size;
        mediaPhase = 'download complete';
        const blobUrl = URL.createObjectURL(blob);
        attachVideo(blobUrl, blobUrl);
        return true;
      })
      .catch(() => {
        mediaPhase = 'download failed';
        window.clearTimeout(bufferingTimer);
        masterRuntime.element.classList.remove('is-buffering');
        masterRuntime.loading = false;
        masterRuntime.element.classList.add('has-media-error');
        return false;
      });
  };

  const setActiveScene = (index: number): void => {
    if (index === activeScene) return;
    activeScene = index;
    const scene = config.scenes[index];
    if (scene) container.style.setProperty('--accent', scene.accent);

    sceneDom.forEach((dom, sceneIndex) => {
      const active = sceneIndex === index;
      dom.navButton.classList.toggle('is-active', active);
      dom.navButton.setAttribute('aria-current', active ? 'step' : 'false');
      dom.routeButton.classList.toggle('is-active', active);
      dom.routeButton.setAttribute('aria-current', active ? 'step' : 'false');
    });
  };

  function read(): void {
    const scrollUnits = (window.scrollY || window.pageYOffset) / viewportHeight;
    const activeIndex = activeSegmentIndex(runtimeSegments, scrollUnits);
    const activeSegment = runtimeSegments[Math.max(0, activeIndex)];
    const activeProgress = activeSegment ? localProgress(activeSegment, scrollUnits) : 0;
    const activeSceneIndex = activeSegment
      ? nearestSceneIndex(activeSegment, activeProgress, config.scenes.length)
      : 0;
    const globalProgress = journeyProgress(scrollUnits, totalUnits);
    const exitProgress = handoffProgress(scrollUnits, totalUnits, handoffUnits);
    const journeyOpacity = 1 - smoothstep(exitProgress);

    container.style.setProperty('--journey-progress', globalProgress.toFixed(4));
    if (isMobile() && masterRuntime) {
      const mobileMediaX = 50 + globalProgress * 7;
      masterRuntime.element.style.setProperty('--mobile-media-x', `${mobileMediaX.toFixed(2)}%`);
    }
    container.style.setProperty('--handoff-progress', exitProgress.toFixed(4));
    container.style.setProperty('--journey-opacity', journeyOpacity.toFixed(4));
    const handoffComplete = exitProgress >= 0.999;
    container.classList.toggle('is-handoff-complete', handoffComplete);
    chrome.header.inert = handoffComplete;
    chrome.copyLayer.inert = handoffComplete;
    chrome.route.inert = handoffComplete;

    if (masterRuntime) {
      loadMasterVideo();
      masterRuntime.element.style.opacity = String(journeyOpacity);
      if (!masterRuntime.ready) {
        masterRuntime.image.style.transform = reducedMotion
          ? 'scale(1.015)'
          : `scale(${(1.015 + globalProgress * 0.015).toFixed(4)})`;
      }
    } else {
      runtimeSegments.forEach((segment, index) => {
        if (scrollUnits > segment.start - 1.5 && scrollUnits < segment.end + 1.5) {
          loadVideo(segment);
        }

        const progress = localProgress(segment, scrollUnits);
        const visualProgress = reducedMotion ? 0.5 : progress;
        const sceneStyle = segment.element.style;
        const scene = config.scenes[segment.sceneIndex];
        const linger = segment.kind === 'scene' ? (scene?.linger ?? 0) : 0;
        segment.target = lingerEase(progress, linger);

        let distance = 0;
        if (scrollUnits < segment.start) distance = segment.start - scrollUnits;
        if (scrollUnits > segment.end) distance = scrollUnits - segment.end;
        const opacity = reducedMotion
          ? index === Math.max(0, activeIndex)
            ? 1
            : 0
          : smoothstep(1 - distance / crossfadeUnits);
        const visible = opacity > 0.001;
        sceneStyle.opacity = String(opacity);
        sceneStyle.visibility = visible ? 'visible' : 'hidden';
        segment.visible = visible;

        if (visible) {
          sceneStyle.setProperty('--scene-progress', progress.toFixed(4));
          sceneStyle.setProperty('--atmosphere-opacity', (0.72 + visualProgress * 0.28).toFixed(4));
          sceneStyle.setProperty('--atmosphere-scale', (1 + visualProgress * 0.08).toFixed(4));
          sceneStyle.setProperty('--arch-opacity', (0.92 - visualProgress * 0.48).toFixed(4));
          sceneStyle.setProperty('--arch-scale', (0.62 + visualProgress * 1.52).toFixed(4));
          sceneStyle.setProperty('--arch-2-opacity', (0.38 - visualProgress * 0.16).toFixed(4));
          sceneStyle.setProperty('--arch-2-scale', (0.36 + visualProgress * 0.8).toFixed(4));
          sceneStyle.setProperty('--arch-3-opacity', (0.18 - visualProgress * 0.08).toFixed(4));
          sceneStyle.setProperty('--arch-3-scale', (0.18 + visualProgress * 0.4).toFixed(4));
          sceneStyle.setProperty('--node-opacity', (0.18 + visualProgress * 0.62).toFixed(4));
          sceneStyle.setProperty('--node-scale', (0.7 + visualProgress * 0.8).toFixed(4));
          sceneStyle.setProperty('--coordinate-opacity', (0.15 + visualProgress * 0.38).toFixed(4));
          sceneStyle.setProperty('--coordinate-rotation', `${(visualProgress * 18).toFixed(2)}deg`);
          sceneStyle.setProperty('--figure-x', `${((0.5 - visualProgress) * 3).toFixed(2)}vw`);
          sceneStyle.setProperty('--figure-y', `${(visualProgress * -2).toFixed(2)}vh`);
          sceneStyle.setProperty('--figure-scale', (0.84 + visualProgress * 0.2).toFixed(4));
          sceneStyle.setProperty('--cloak-skew', `${((0.5 - visualProgress) * 4).toFixed(2)}deg`);
          sceneStyle.setProperty('--particle-opacity', (0.3 + visualProgress * 0.6).toFixed(4));
          sceneStyle.setProperty('--particle-shift', `${(visualProgress * -22).toFixed(2)}px`);
        }

        if (visible && !segment.ready) {
          const scale = reducedMotion ? 1 : 1.025 + progress * 0.11;
          segment.image.style.transform = `scale(${scale.toFixed(3)})`;
        }
      });
    }

    config.scenes.forEach((_, index) => {
      const segment = sceneSegments.get(index);
      const dom = sceneDom[index];
      if (!segment || !dom) return;
      const progress = localProgress(segment, scrollUnits);
      const before = scrollUnits < segment.start;
      const after = scrollUnits > segment.end;
      let opacity = 0;

      if (reducedMotion) opacity = index === activeSceneIndex ? 1 : 0;
      else {
        if (index === 0) opacity = after ? 0 : smoothstep(1 - progress / 0.62);
        else if (index === config.scenes.length - 1) {
          opacity = before ? 0 : smoothstep(progress / 0.4);
        } else if (!before && !after) {
          opacity = smoothstep(1 - Math.abs(progress - 0.5) / 0.5);
        }
      }

      dom.copy.style.opacity = String(opacity);
      dom.copy.style.transform = isMobile()
        ? reducedMotion
          ? 'none'
          : `translateY(calc(${(0.5 - progress) * 1.5}vh + var(--snap-offset, 0px)))`
        : reducedMotion
          ? 'translateY(-50%)'
          : `translateY(calc(-50% + ${(0.5 - progress) * 4}vh + var(--snap-offset, 0px)))`;
      dom.copy.style.pointerEvents = opacity > 0.55 ? 'auto' : 'none';
      dom.copy.setAttribute('aria-hidden', opacity > 0.55 ? 'false' : 'true');
    });

    if (activeSegment) setActiveScene(activeSceneIndex);

    chrome.progressFill.style.transform = `scaleX(${globalProgress})`;
    chrome.hint.style.opacity = String(clamp(1 - scrollUnits / 0.55) * journeyOpacity);

    if (debug && activeSegment) {
      const progress = localProgress(activeSegment, scrollUnits);
      const scene = config.scenes[activeSegment.sceneIndex];
      const masterVideo = masterRuntime?.video;
      const duration = masterVideo?.duration ?? activeSegment.video?.duration;
      const mediaProgress = masterRuntime ? masterRuntime.current : activeSegment.target;
      debug.scene.textContent = `scene ${String(activeSegment.sceneIndex + 1).padStart(2, '0')} / ${scene?.id ?? 'unknown'}`;
      debug.global.textContent = `global ${globalProgress.toFixed(3)} / exit ${exitProgress.toFixed(3)}`;
      debug.local.textContent = `local ${progress.toFixed(3)} / target ${activeSegment.target.toFixed(3)}`;
      debug.media.textContent = duration
        ? `video ${masterVideo?.currentTime.toFixed(2) ?? (mediaProgress * duration).toFixed(2)}s / ${duration.toFixed(2)}s`
        : 'media local animatic';
      debug.flags.textContent = `mobile ${isMobile()} / coarse ${coarsePointer} / reduce ${reducedMotion}`;
      debug.state.textContent = masterVideo
        ? `state r${masterVideo.readyState} n${masterVideo.networkState} / ${masterVideo.paused ? 'paused' : 'active'}${masterVideo.seeking ? ' / seeking' : ''}`
        : `state no video / ${(loadedBytes / 1_000_000).toFixed(1)} MB`;
      debug.event.textContent = `${mediaPhase} / ${lastMediaEvent} / ${playState}`;
      debug.root.classList.toggle('is-after-journey', handoffComplete);
    }
    requestAnimate();
    ticking = false;
  }

  function requestAnimate(): void {
    const mediaReady = masterRuntime?.ready || runtimeSegments.some((segment) => segment.ready);
    if (videoFramePending || !mediaReady) return;
    videoFramePending = true;
    window.requestAnimationFrame(animate);
  }

  function animate(): void {
    videoFramePending = false;
    const seekThreshold = isMobile() ? 0.02 : 0.008;
    let unsettled = false;

    if (masterRuntime?.video && masterRuntime.ready) {
      // Magnetic navigation owns the master video's playback. Unlike the
      // fallback per-scene renderer, scrolling never seeks it frame by frame.
      return;
    }

    for (const segment of runtimeSegments) {
      const video = segment.video;
      if (!video || !segment.ready) continue;
      if (video.seeking) {
        unsettled = true;
        continue;
      }
      if (!segment.visible && Math.abs(segment.current - segment.target) < 0.002) continue;

      segment.current += (segment.target - segment.current) * (reducedMotion ? 1 : seekSmoothing);
      const targetTime = clamp(segment.current, 0, 0.999) * (video.duration || 1);
      if (Math.abs(video.currentTime - targetTime) > seekThreshold) {
        try {
          video.currentTime = targetTime;
          unsettled = true;
        } catch {
          // A browser can reject seeks while metadata changes; the next frame retries.
        }
      }
    }
    if (unsettled) requestAnimate();
  }

  const pointTime = (index: number, video: HTMLVideoElement): number =>
    mediaSnapProgress(index, config.scenes.length) * Math.max(0, (video.duration || 1) - 0.04);

  function syncMasterToPoint(index: number): void {
    const video = masterRuntime?.video;
    if (!video || !masterRuntime.ready || index < 0) return;
    const progress = mediaSnapProgress(index, config.scenes.length);
    const targetTime = pointTime(index, video);
    video.pause();
    masterRuntime.current = progress;
    masterRuntime.target = progress;
    if (Math.abs(video.currentTime - targetTime) > 0.025) {
      try {
        video.currentTime = targetTime;
      } catch {
        // The next point transition retries once metadata is stable.
      }
    }
  }

  const setSnapFeedback = (amount: number, direction = 1): void => {
    const tension = clamp(amount);
    container.style.setProperty('--snap-tension', tension.toFixed(3));
    container.style.setProperty('--snap-direction', String(direction));
    container.style.setProperty('--snap-offset', `${(tension * direction * -12).toFixed(2)}px`);
  };

  const clearSnapFeedback = (): void => {
    wheelTension = 0;
    setSnapFeedback(0);
  };

  const seekAndWait = (video: HTMLVideoElement, time: number, token: number): Promise<void> =>
    new Promise((resolve) => {
      if (Math.abs(video.currentTime - time) < 0.025 || transitionToken !== token) {
        resolve();
        return;
      }

      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        video.removeEventListener('seeked', finish);
        window.clearTimeout(timeout);
        resolve();
      };
      const timeout = window.setTimeout(finish, 700);
      video.addEventListener('seeked', finish, { once: true });
      try {
        video.currentTime = time;
      } catch {
        finish();
      }
    });

  const waitForMasterMetadata = (video: HTMLVideoElement, token: number): Promise<boolean> =>
    new Promise((resolve) => {
      if (masterRuntime?.ready) {
        resolve(true);
        return;
      }

      let settled = false;
      const finish = (ready: boolean): void => {
        if (settled) return;
        settled = true;
        video.removeEventListener('loadedmetadata', handleReady);
        video.removeEventListener('error', handleError);
        window.clearTimeout(timeout);
        resolve(transitionToken === token && ready);
      };
      const handleReady = (): void => finish(true);
      const handleError = (): void => finish(false);
      const timeout = window.setTimeout(() => finish(false), 2500);
      video.addEventListener('loadedmetadata', handleReady, { once: true });
      video.addEventListener('error', handleError, { once: true });
    });

  const updateTransitionScroll = (from: number, to: number, progress: number): void => {
    const eased = smoothstep(progress);
    const scrollUnits = from + (to - from) * eased;
    window.scrollTo({ top: scrollUnits * viewportHeight, behavior: 'auto' });
    setSnapFeedback(Math.sin(Math.PI * progress) * 0.42, Math.sign(to - from) || 1);
  };

  const markTransition = (transition: MagneticTransition): void => {
    magneticTransition = transition;
    document.documentElement.classList.add('has-magnetic-control');
    container.classList.add('is-magnetic-transition');
    container.classList.toggle('is-magnetic-reverse', transition.kind === 'reverse');
    sceneDom.forEach((dom, index) => {
      dom.routeButton.classList.toggle('is-target', index === transition.to);
    });
    clearSnapFeedback();
  };

  const finishPointTransition = (target: number, token: number): void => {
    if (transitionToken !== token) return;
    const video = masterRuntime?.video;
    settledPoint = target;
    magneticTransition = undefined;
    container.classList.remove('is-magnetic-transition', 'is-magnetic-reverse');
    container.style.removeProperty('--media-opacity');
    document.documentElement.classList.remove('has-magnetic-control');
    sceneDom.forEach((dom) => {
      dom.routeButton.classList.remove('is-target');
    });
    clearSnapFeedback();
    window.scrollTo({ top: (snapAnchors[target] ?? 0) * viewportHeight, behavior: 'auto' });
    setActiveScene(target);
    if (video && masterRuntime) {
      video.pause();
      masterRuntime.current = mediaSnapProgress(target, config.scenes.length);
      masterRuntime.target = masterRuntime.current;
    }
    requestRead();
  };

  const runTimedScroll = (
    fromUnits: number,
    toUnits: number,
    duration: number,
    token: number,
    done: () => void,
  ): void => {
    const started = performance.now();
    const step = (now: number): void => {
      if (transitionToken !== token) return;
      const progress = clamp((now - started) / duration);
      updateTransitionScroll(fromUnits, toUnits, progress);
      if (progress < 1) window.requestAnimationFrame(step);
      else done();
    };
    window.requestAnimationFrame(step);
  };

  const runSeekedPlayback = (
    video: HTMLVideoElement,
    fromTime: number,
    toTime: number,
    fromUnits: number,
    toUnits: number,
    token: number,
  ): void => {
    const duration = 1800;
    const started = performance.now();
    let lastSeekAt = -Infinity;
    video.pause();
    masterRuntime?.element.classList.remove('is-buffering');
    masterRuntime?.element.classList.add('has-video');

    const step = (now: number): void => {
      if (transitionToken !== token) return;
      const progress = clamp((now - started) / duration);
      const targetTime = fromTime + (toTime - fromTime) * progress;

      // Limit fallback seeks so constrained mobile decoders can finish each
      // frame instead of accumulating a request on every animation frame.
      if (!video.seeking && now - lastSeekAt >= 80) {
        try {
          video.currentTime = targetTime;
          lastSeekAt = now;
        } catch {
          // Continue the spatial transition even if one individual seek fails.
        }
      }

      if (masterRuntime) {
        masterRuntime.current =
          mediaSnapProgress(settledPoint, config.scenes.length) +
          (mediaSnapProgress(magneticTransition?.to ?? settledPoint, config.scenes.length) -
            mediaSnapProgress(settledPoint, config.scenes.length)) *
            progress;
      }
      updateTransitionScroll(fromUnits, toUnits, progress);

      if (progress < 1) {
        window.requestAnimationFrame(step);
        return;
      }

      void seekAndWait(video, toTime, token).then(() => {
        const target = magneticTransition?.to;
        if (target !== undefined) finishPointTransition(target, token);
      });
    };

    window.requestAnimationFrame(step);
  };

  const maskAndSeek = async (target: number, token: number): Promise<void> => {
    const video = masterRuntime?.video;
    if (!video || !masterRuntime?.ready) return;
    container.style.setProperty('--media-opacity', '0.12');
    await new Promise<void>((resolve) => window.setTimeout(resolve, 150));
    if (transitionToken !== token) return;
    await seekAndWait(video, pointTime(target, video), token);
    if (transitionToken === token) container.style.setProperty('--media-opacity', '1');
  };

  const runForwardPlayback = async (from: number, to: number, token: number): Promise<void> => {
    let video = masterRuntime?.video;
    const fromUnits = snapAnchors[from] ?? 0;
    const toUnits = snapAnchors[to] ?? fromUnits;

    if (!video && masterLoadPromise) {
      const mediaAttached = await Promise.race([
        masterLoadPromise,
        new Promise<false>((resolve) => window.setTimeout(() => resolve(false), 20000)),
      ]);
      if (transitionToken !== token) return;
      if (mediaAttached) video = masterRuntime?.video;
    }

    if (video && !masterRuntime?.ready) {
      if (isMobile()) {
        // Start the muted inline playback request while the swipe still owns
        // user activation; metadata can arrive while that request is pending.
        try {
          void video.play().catch(() => undefined);
        } catch {
          // The normal timed fallback remains available below.
        }
      }
      const metadataReady = await waitForMasterMetadata(video, token);
      if (transitionToken !== token) return;
      if (metadataReady) video = masterRuntime?.video;
    }

    if (!video || !masterRuntime?.ready) {
      runTimedScroll(fromUnits, toUnits, 1100, token, () => {
        syncMasterToPoint(to);
        finishPointTransition(to, token);
      });
      return;
    }

    if (!isMobile()) video.pause();
    const startTime = pointTime(from, video);
    const endTime = pointTime(to, video);
    video.playbackRate = 1;

    if (Math.abs(video.currentTime - startTime) > 0.025) {
      try {
        video.currentTime = startTime;
      } catch {
        // Playback below remains the authoritative recovery path.
      }
    }

    let playback: Promise<void>;
    try {
      // Invoke play before the first await so strict mobile browsers still
      // associate it with the originating swipe gesture.
      playState = 'transition pending';
      playback = video.play();
    } catch {
      playState = 'transition threw / seek fallback';
      runSeekedPlayback(video, startTime, endTime, fromUnits, toUnits, token);
      return;
    }

    try {
      await playback;
    } catch {
      playState = 'transition denied / seek fallback';
      runSeekedPlayback(video, startTime, endTime, fromUnits, toUnits, token);
      return;
    }

    if (transitionToken !== token) {
      video.pause();
      return;
    }

    masterRuntime.element.classList.remove('is-buffering');
    masterRuntime.element.classList.add('has-video');
    playState = 'transition playing';

    let lastMediaTime = video.currentTime;
    let lastAdvanceAt = performance.now();
    const step = (): void => {
      if (transitionToken !== token) {
        video.pause();
        return;
      }
      const progress = clamp(
        (video.currentTime - startTime) / Math.max(0.001, endTime - startTime),
      );
      if (video.currentTime > lastMediaTime + 0.002) {
        lastMediaTime = video.currentTime;
        lastAdvanceAt = performance.now();
      } else if (performance.now() - lastAdvanceAt > 850) {
        video.pause();
        playState = 'clock stalled / seek fallback';
        runSeekedPlayback(
          video,
          video.currentTime,
          endTime,
          window.scrollY / viewportHeight,
          toUnits,
          token,
        );
        return;
      }
      masterRuntime.current =
        mediaSnapProgress(from, config.scenes.length) +
        (mediaSnapProgress(to, config.scenes.length) -
          mediaSnapProgress(from, config.scenes.length)) *
          progress;
      updateTransitionScroll(fromUnits, toUnits, progress);
      if (video.currentTime >= endTime - 0.018 || progress >= 0.999) {
        video.pause();
        try {
          video.currentTime = endTime;
        } catch {
          // The visible frame is already within one frame of the boundary.
        }
        finishPointTransition(to, token);
        return;
      }
      window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  };

  const startPointTransition = (target: number, direct = false): void => {
    if (magneticTransition || target < 0 || target >= config.scenes.length) return;
    const from = settledPoint;
    const targetUnits = snapAnchors[target] ?? 0;
    const alreadyAtTarget = Math.abs(window.scrollY / viewportHeight - targetUnits) < 0.04;
    if (from === target && alreadyAtTarget) {
      window.scrollTo({ top: (snapAnchors[target] ?? 0) * viewportHeight, behavior: 'auto' });
      syncMasterToPoint(target);
      return;
    }

    const token = ++transitionToken;
    const kind: MagneticTransition['kind'] = direct
      ? 'direct'
      : target > from
        ? 'forward'
        : 'reverse';
    markTransition({ from, to: target, token, kind });

    if (!direct && target === from + 1 && !reducedMotion) {
      void runForwardPlayback(from, target, token);
      return;
    }

    if (!reducedMotion) void maskAndSeek(target, token);
    else syncMasterToPoint(target);
    runTimedScroll(
      direct ? window.scrollY / viewportHeight : (snapAnchors[from] ?? 0),
      snapAnchors[target] ?? 0,
      reducedMotion ? 1 : direct ? 760 : 900,
      token,
      () => finishPointTransition(target, token),
    );
  };

  const startHandoff = (): void => {
    if (magneticTransition) return;
    const token = ++transitionToken;
    markTransition({
      from: settledPoint,
      to: settledPoint,
      token,
      kind: 'handoff',
    });
    const afterworldTop = chrome.track.offsetHeight / viewportHeight;
    runTimedScroll(
      snapAnchors[settledPoint] ?? totalUnits,
      afterworldTop,
      reducedMotion ? 1 : 950,
      token,
      () => {
        if (transitionToken !== token) return;
        magneticTransition = undefined;
        container.classList.remove('is-magnetic-transition', 'is-magnetic-reverse');
        sceneDom.forEach((dom) => {
          dom.routeButton.classList.remove('is-target');
        });
        document.documentElement.classList.remove('has-magnetic-control');
        clearSnapFeedback();
        window.scrollTo({ top: chrome.track.offsetHeight, behavior: 'auto' });
      },
    );
  };

  const returnFromHandoff = (): void => {
    if (magneticTransition) return;
    const token = ++transitionToken;
    const target = config.scenes.length - 1;
    const fromUnits = window.scrollY / viewportHeight;
    markTransition({ from: target, to: target, token, kind: 'direct' });
    syncMasterToPoint(target);
    runTimedScroll(
      fromUnits,
      snapAnchors[target] ?? totalUnits,
      reducedMotion ? 1 : 720,
      token,
      () => finishPointTransition(target, token),
    );
  };

  const moveByDirection = (direction: -1 | 1): void => {
    const lastPoint = config.scenes.length - 1;
    if (direction > 0 && settledPoint === lastPoint) {
      startHandoff();
      return;
    }
    if (
      direction < 0 &&
      window.scrollY > ((snapAnchors[lastPoint] ?? totalUnits) + 0.08) * viewportHeight
    ) {
      returnFromHandoff();
      return;
    }
    startPointTransition(clamp(settledPoint + direction, 0, lastPoint));
  };

  const journeyOwnsInput = (): boolean =>
    window.scrollY <= (totalUnits + handoffUnits + 0.04) * viewportHeight;

  const interactiveTarget = (target: EventTarget | null): boolean =>
    target instanceof Element &&
    Boolean(target.closest('a, button, input, textarea, select, [contenteditable="true"]'));

  const handleWheel = (event: WheelEvent): void => {
    if (!journeyOwnsInput() || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY))
      return;
    event.preventDefault();
    if (magneticTransition || event.deltaY === 0) return;

    const multiplier =
      event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? viewportHeight
        : event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 18
          : 1;
    const delta = event.deltaY * multiplier;
    const direction: -1 | 1 = delta < 0 ? -1 : 1;
    wheelTension = clamp(wheelTension + Math.abs(delta) / 88);
    setSnapFeedback(wheelTension, direction);
    window.clearTimeout(wheelResetTimer);
    wheelResetTimer = window.setTimeout(clearSnapFeedback, 180);
    if (wheelTension >= 1) {
      window.clearTimeout(wheelResetTimer);
      clearSnapFeedback();
      moveByDirection(direction);
    }
  };

  const handleTouchStart = (event: TouchEvent): void => {
    if (!journeyOwnsInput() || event.touches.length !== 1) return;
    touchStartY = event.touches[0]?.clientY;
    touchTriggered = false;
  };

  const handleTouchMove = (event: TouchEvent): void => {
    const currentY = event.touches[0]?.clientY;
    if (touchStartY === undefined || currentY === undefined || !journeyOwnsInput()) return;
    const delta = touchStartY - currentY;
    if (Math.abs(delta) < 3) return;
    event.preventDefault();
    if (magneticTransition || touchTriggered) return;
    const direction: -1 | 1 = delta < 0 ? -1 : 1;
    const tension = clamp(Math.abs(delta) / 64);
    setSnapFeedback(tension, direction);
    if (tension >= 1) {
      touchTriggered = true;
      clearSnapFeedback();
      moveByDirection(direction);
    }
  };

  const handleTouchEnd = (): void => {
    touchStartY = undefined;
    touchTriggered = false;
    if (!magneticTransition) clearSnapFeedback();
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    if (
      !journeyOwnsInput() ||
      interactiveTarget(event.target) ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey
    )
      return;
    const down = event.key === 'ArrowDown' || event.key === 'PageDown' || event.key === ' ';
    const up = event.key === 'ArrowUp' || event.key === 'PageUp';
    if (!down && !up) return;
    event.preventDefault();
    if (!magneticTransition) moveByDirection(down ? 1 : -1);
  };

  const layout = (): void => {
    viewportHeight = window.innerHeight;
    laidOutWidth = window.innerWidth;
    totalUnits = runtimeSegments.at(-1)?.end ?? 1;
    snapAnchors = sceneSnapAnchors(runtimeSegments, config.scenes.length);
    chrome.track.style.height = `${(totalUnits + handoffUnits + 1) * viewportHeight}px`;
    read();
  };

  const requestRead = (): void => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(read);
  };

  const handleResize = (): void => {
    if (coarsePointer && window.innerWidth === laidOutWidth) return;
    layout();
  };

  const handleFirstGesture = (): void => {
    if (gestureReceived) return;
    gestureReceived = true;
    primeVideo(masterRuntime?.video);
    for (const segment of runtimeSegments) primeVideo(segment.video);
  };

  sceneDom.forEach((dom, index) => {
    dom.navButton.addEventListener('click', () => startPointTransition(index, true));
    dom.routeButton.addEventListener('click', () => startPointTransition(index, true));
  });

  container.addEventListener('click', (event) => {
    const anchor =
      event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a') : null;
    if (anchor?.hash !== '#top') return;
    event.preventDefault();
    startPointTransition(0, true);
  });

  window.addEventListener('scroll', requestRead, { passive: true });
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', layout);
  window.addEventListener('wheel', handleWheel, { passive: false });
  window.addEventListener('keydown', handleKeydown);
  window.addEventListener('touchstart', handleTouchStart, { passive: true });
  window.addEventListener('touchmove', handleTouchMove, { passive: false });
  window.addEventListener('touchend', handleTouchEnd, { passive: true });
  window.addEventListener('touchcancel', handleTouchEnd, { passive: true });
  window.addEventListener('pointerdown', handleFirstGesture, { once: true, passive: true });
  window.addEventListener('touchstart', handleFirstGesture, { once: true, passive: true });

  layout();
  if (playHeroEntrance) {
    window.setTimeout(() => container.classList.remove('is-hero-entering'), 2400);
  }
}
